import React from "react";

// All Z marks live inside a 24×24 viewBox, single-color via currentColor.
// Optimized so bar height stays ≥ 16% of glyph height (readable at 16px favicon).

// --- Z / 01 · MONOLITH -------------------------------------------------
// Uniform-weight geometric Z. All three strokes (top bar, diagonal,
// bottom bar) share the exact same visual thickness. Softly rounded
// joins and endcaps — no sharp mitered points.
export const ZMonolith = ({ className = "", title = "Zaplee Z monolith" }) => (
  <svg
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
    aria-label={title}
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="4.25"
    strokeLinejoin="round"
    strokeLinecap="round"
  >
    <path d="M9 9 H23 L9 23 H23" />
  </svg>
);

// --- Z / 02 · BOLT -----------------------------------------------------
// (Deprecated variant — kept for reference but no longer surfaced in Lab.)
export const ZBolt = ({ className = "", title = "Zaplee Z bolt" }) => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-label={title}
    className={className}
    fill="currentColor"
  >
    <path d="M4 4 H20 V8 L13 13 H17 L8 16 H20 V20 H4 V16 L11 11 H7 L16 8 H4 V4 Z" />
  </svg>
);

// --- Z / 03 · SLICE ----------------------------------------------------
// Z with a horizontal cut through the diagonal. Uniform stroke weight
// (matches Monolith), rounded joins/caps, then split by a thin gap.
export const ZSlice = ({ className = "", title = "Zaplee Z slice" }) => (
  <svg
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
    aria-label={title}
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="4.25"
    strokeLinejoin="round"
    strokeLinecap="round"
  >
    {/* Top bar + upper half of diagonal */}
    <path d="M9 9 H23 L17 15" />
    {/* Lower half of diagonal + bottom bar */}
    <path d="M15 17 L9 23 H23" />
  </svg>
);

/**
 * ZInCircle
 * Renders the chosen Z mark inside a perfect circle.
 * @param {"monolith"|"bolt"|"slice"} variant
 * @param {"filled"|"outline"|"ring"} style
 * @param {string} size Tailwind sizing pair, e.g. "h-16 w-16"
 * @param {string} bg Tailwind bg (defaults to black)
 * @param {string} fg Tailwind text color for the Z (defaults to white)
 * @param {string} innerRatio One of "loose" | "snug" | "tight" — Z inset
 */
export const ZInCircle = ({
  variant = "monolith",
  look = "filled",
  size = "h-16 w-16",
  bg = "bg-zinc-950",
  fg = "text-white",
  innerRatio = "snug",
  className = "",
  ...rest
}) => {
  const Mark =
    variant === "bolt" ? ZBolt : variant === "slice" ? ZSlice : ZMonolith;

  const ratioClass = {
    loose: "h-[38%] w-[38%]",
    snug: "h-[44%] w-[44%]",
    tight: "h-[52%] w-[52%]",
  }[innerRatio];

  const containerBase = "rounded-full grid place-items-center shrink-0";
  const styleClass =
    look === "outline"
      ? `border-[1.5px] border-zinc-950 bg-transparent text-zinc-950`
      : look === "ring"
      ? `${bg} ${fg} ring-1 ring-zinc-200`
      : `${bg} ${fg}`;

  return (
    <span
      className={`${containerBase} ${styleClass} ${size} ${className}`}
      {...rest}
    >
      <Mark className={`${ratioClass} block`} />
    </span>
  );
};

// Wordmark — reused across the exploration.
export const Wordmark = ({
  className = "",
  accentClass = "text-[#0047FF]",
  size = "text-lg",
}) => (
  <span
    className={`font-display font-semibold tracking-tight ${size} ${className}`}
  >
    Zaplee<span className={accentClass}>.ai</span>
  </span>
);
